import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'

const API_VERSION = 'v1'
const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api/${API_VERSION}/user`

export const findGuests = createAsyncThunk(
	'api/venue/findGuests',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/get-guests?searchQuery=${data.searchQuery}&page=${data.page}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const getKazooGuests = createAsyncThunk(
	'kazoo/getKazooGuests',
	async (kazooId) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/get-kazoo-guests/${kazooId}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const addGuestToKazoo = createAsyncThunk(
	'kazoo/addGuestToKazoo',
	async ({ kazooId, email, roleInWedding, isWeddingMember, permissions }) => {
		try {
			const res = await fetch(`${REQUEST_URL}/add-guest/${kazooId}`, {
				method: 'PATCH',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ email, roleInWedding, isWeddingMember, permissions }),
			})
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const removeMemberFromKazoo = createAsyncThunk(
	'kazoo/removeMemberFromKazoo',
	async ({ kazooId, userId, inviteId, email }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/remove-member-from-kazoo/${kazooId}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ userId, inviteId, email }),
				}
			)
			const data = await res.json()
			return data
		} catch (err) {
			console.error(err.message)
		}
	}
)

export const updateKazooGuest = createAsyncThunk(
  'kazoo/updateKazooGuest',
  async ({ kazooId, edgeId, userId, email, roleInWedding, isWeddingMember, permissions }) => {
    try {
      const res = await fetch(`${REQUEST_URL}/update-guest/${kazooId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ edgeId, userId, email, roleInWedding, isWeddingMember, permissions }),
      })
      const data = await res.json()
      return data
    } catch (err) {
      console.error(err.message)
    }
  }
)


const initialState = {
	guestSearchList: [],
	guests: [],
}

const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(findGuests.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload.message || 'Failed to fetch for guests',
						'error'
					)
					return
				}

				state.guestSearchList = payload.guests
			})

			.addCase(getKazooGuests.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to fetch wedding guests',
						'error'
					)
					return
				}

				state.guests = payload.guests
			})

			.addCase(addGuestToKazoo.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload.message || 'Failed to add guest',
						'error'
					)
					return
				}

				toastHelper(
					payload.message || 'Successfully added guest to wedding',
					'success'
				)

				const g = payload.guest
				if (!g) return

				if (g._id) {
					const idx = state.guests.findIndex((x) => x._id === g._id)
					if (idx >= 0)
						state.guests[idx] = { ...state.guests[idx], ...g }
					else state.guests.push(g)
				} else if (g.email) {
					const idx = state.guests.findIndex(
						(x) => x.email === g.email
					)
					if (idx >= 0)
						state.guests[idx] = { ...state.guests[idx], ...g }
					else state.guests.push(g)
				}
			})

			.addCase(removeMemberFromKazoo.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message ||
							'Could not remove member from wedding',
						'error'
					)
					return
				}

				toastHelper(
					payload.message ||
						'Successfully removed member from wedding',
					'success'
				)

				const { userId, inviteId, email } = payload

				state.guests = state.guests.filter((g) => {
					if (userId) return g._id !== userId
					if (inviteId) return g._inviteId !== inviteId
					if (email)
						return (
							(g.email || '').toLowerCase() !==
							String(email).toLowerCase()
						)
					return true
				})
			})

			.addCase(updateKazooGuest.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(payload?.message || 'Failed to update guest', 'error')
					return
				}
				toastHelper(payload.message || 'Guest updated', 'success')

				const g = payload.guest
				if (!g) return

				// Try match by user _id, inviteId, or email 
				let idx = -1
				if (g._id) idx = state.guests.findIndex(x => x._id === g._id)
				if (idx < 0 && g._inviteId) idx = state.guests.findIndex(x => x._inviteId === g._inviteId)
				if (idx < 0 && g.email) {
					const target = String(g.email).toLowerCase()
					idx = state.guests.findIndex(x => String(x.email || '').toLowerCase() === target)
				}

				if (idx >= 0) {
					state.guests[idx] = { ...state.guests[idx], ...g }
				} else {
					state.guests.push(g)
				}
			})

	},
})

export default userSlice.reducer
